import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { CARD_MACHINE_PRODUCTS } from "@/data/cardMachineProducts";

/**
 * Semantic internal-link block: parent hub, sibling products (min 2),
 * relevant services and industries. Keeps the cluster densely
 * cross-linked without stuffing the main navigation.
 */
export default function RelatedProductLinks({ product }) {
  const siblings = (product.relatedProducts || [])
    .map((slug) => CARD_MACHINE_PRODUCTS[slug])
    .filter(Boolean);

  return (
    <section className="py-16 md:py-20 bg-white" data-testid={`${product.slug}-related`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-8">
          Related solutions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {siblings.map((sib) => (
            <Link
              key={sib.slug}
              to={`/card-machines/${sib.slug}`}
              className="group bg-gray-50 border border-brand-light-grey rounded-2xl p-6 flex flex-col hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              data-testid={`related-product-${sib.slug}`}
            >
              <div className="h-36 flex items-center justify-center mb-4">
                <img src={sib.heroImage} alt={sib.heroImageAlt} className="max-h-32 object-contain" loading="lazy" />
              </div>
              <h3 className="font-heading text-lg font-bold text-brand-black mb-1">{sib.name}</h3>
              <p className="text-sm text-brand-grey flex-1">{`${sib.quickAnswer.answer.split(". ")[0]}.`}</p>
              <span className="mt-4 text-sm font-semibold text-brand-aqua flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                {`View ${sib.shortName}`}
                <ArrowRight size={14} />
              </span>
            </Link>
          ))}
          <div className="bg-gray-50 border border-brand-light-grey rounded-2xl p-6 flex flex-col">
            <h3 className="font-heading text-lg font-bold text-brand-black mb-3">Explore more from CanDo</h3>
            <ul className="space-y-2.5 flex-1">
              <li>
                <Link to="/card-machines" className="text-sm text-brand-aqua hover:text-brand-aqua-dark font-semibold inline-flex items-center gap-1 transition-colors">
                  All card machines <ArrowUpRight size={13} />
                </Link>
              </li>
              {(product.relatedServices || []).map((s) => (
                <li key={s.href}>
                  <Link to={s.href} className="text-sm text-brand-aqua hover:text-brand-aqua-dark font-semibold inline-flex items-center gap-1 transition-colors">
                    {s.label}
                    <ArrowUpRight size={13} />
                  </Link>
                </li>
              ))}
              {(product.relatedIndustries || []).map((s) => (
                <li key={s.href}>
                  <Link to={s.href} className="text-sm text-brand-aqua hover:text-brand-aqua-dark font-semibold inline-flex items-center gap-1 transition-colors">
                    {`For ${s.label}`}
                    <ArrowUpRight size={13} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
